import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from '../Image';
import { ReactComponent as IconCalendar } from '../../assets/images/icons/icon_calendar.svg';
import * as types from '../../types';
import styles from './RescueCartItem.module.scss';

export interface RescueCartItemProps {
  cartItemId: types.CartItemID;
  productImageUrl: string;
  /** The collar row: product name, variant description ("Gray"), and its price in the bundle. */
  title: string;
  subtitle?: string;
  price: string;
  /** The membership row, stated as its own line item rather than a sub-row of the collar. */
  membershipName: string;
  membershipPrice: string;
  membershipOriginalPrice?: string;
  membershipNote?: string;
  removeCartItem?: () => void;
}

/**
 * The rescue cart's item rows.
 *
 * The shared `CartItemV2` nests hardware, membership and the activation fee as sub-rows of one
 * collar card. The rescue cart states each line at the same level instead - the membership is the
 * gift, so burying it inside the collar reads as a detail of the hardware rather than the thing
 * being given - and the activation fee moves to the totals block, where the one charge an adopter
 * actually pays belongs. Kept separate from `CartItemV2` deliberately: this shape is wrong for
 * every other cart, and threading it through as flags would put a rescue branch on the component
 * every cart renders.
 */
export default function RescueCartItem({
  cartItemId,
  productImageUrl,
  title,
  subtitle,
  price,
  membershipName,
  membershipPrice,
  membershipOriginalPrice,
  membershipNote,
  removeCartItem,
}: RescueCartItemProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.rescueCartItem} data-cart-item-id={cartItemId}>
      <div className={styles.itemRow}>
        <div className={styles.thumbnail}>
          <Image className={styles.productImage} image={{ type: 'image', url: productImageUrl }} alt={title} />
        </div>
        <div className={styles.rowContent}>
          <div className={styles.rowTitle}>{title}</div>
          {subtitle && <div className={styles.rowSubtitle}>{subtitle}</div>}
        </div>
        <div className={styles.rowPrice}>{price}</div>
      </div>

      <div className={styles.itemRow}>
        <div className={styles.thumbnail}>
          <IconCalendar className={styles.membershipIcon} />
        </div>
        <div className={styles.rowContent}>
          <div className={styles.rowTitle}>{membershipName}</div>
          {membershipNote && <div className={styles.rowNote}>{membershipNote}</div>}
        </div>
        <div className={styles.rowPrice}>
          {membershipOriginalPrice && <span className={styles.originalPrice}>{membershipOriginalPrice}</span>}
          {membershipPrice}
        </div>
      </div>

      {removeCartItem && (
        <button type="button" className={styles.removeLink} onClick={removeCartItem}>
          {t('cart_item_v2.remove')}
        </button>
      )}
    </div>
  );
}
